'use client';

import { useMemo } from 'react';
import { PortableText } from '@portabletext/react';
import { Box, Typography, Link, useTheme } from '@mui/material';
import type {
  PortableTextContent,
  PortableTextBlock,
  PortableTextChild,
  PortableTextMarkDef,
} from './portableText';

export interface PortableTextViewerProps {
  content: PortableTextContent;
}

const URL_REGEX = /https?:\/\/[^\s]+/g;

let autoLinkKeyCounter = 0;
const generateAutoLinkKey = (): string => {
  autoLinkKeyCounter += 1;
  return `autolink${autoLinkKeyCounter}`;
};

const autoLinkContent = (content: PortableTextContent): PortableTextContent => {
  autoLinkKeyCounter = 0;

  return content.map((block): PortableTextBlock => {
    const newMarkDefs: PortableTextMarkDef[] = [...block.markDefs];
    const newChildren: PortableTextChild[] = [];

    for (const child of block.children) {
      if (child._type !== 'span' || child.marks.length > 0) {
        newChildren.push(child);
        continue;
      }

      const text = child.text;
      const matches = [...text.matchAll(URL_REGEX)];

      if (matches.length === 0) {
        newChildren.push(child);
        continue;
      }

      let lastIndex = 0;
      for (const match of matches) {
        const url = match[0];
        const startIndex = match.index!;

        if (startIndex > lastIndex) {
          newChildren.push({
            _key: generateAutoLinkKey(),
            _type: 'span',
            marks: [],
            text: text.slice(lastIndex, startIndex),
          });
        }

        const linkKey = generateAutoLinkKey();
        newMarkDefs.push({
          _key: linkKey,
          _type: 'link',
          href: url,
        });
        newChildren.push({
          _key: generateAutoLinkKey(),
          _type: 'span',
          marks: [linkKey],
          text: url,
        });

        lastIndex = startIndex + url.length;
      }

      if (lastIndex < text.length) {
        newChildren.push({
          _key: generateAutoLinkKey(),
          _type: 'span',
          marks: [],
          text: text.slice(lastIndex),
        });
      }
    }

    return {
      ...block,
      markDefs: newMarkDefs,
      children: newChildren,
    };
  });
};

export const PortableTextViewer = ({ content }: PortableTextViewerProps) => {
  const theme = useTheme();

  const processedContent = useMemo(() => autoLinkContent(content), [content]);

  const components = {
    marks: {
      strong: ({ children }: { children?: React.ReactNode }) => (
        <Typography
          component='strong'
          sx={{
            fontWeight: 600,
          }}
        >
          {children}
        </Typography>
      ),
      em: ({ children }: { children?: React.ReactNode }) => (
        <Typography
          component='em'
          sx={{
            fontStyle: 'italic',
          }}
        >
          {children}
        </Typography>
      ),
      underline: ({ children }: { children?: React.ReactNode }) => (
        <Typography component='span' sx={{ textDecoration: 'underline' }}>
          {children}
        </Typography>
      ),
      link: ({
        value,
        children,
      }: {
        value?: { href?: string };
        children?: React.ReactNode;
      }) => {
        const href = value?.href;
        if (!href) return <>{children}</>;
        return (
          <Link
            component='a'
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            color='primary'
            underline='always'
            sx={{ cursor: 'pointer' }}
          >
            {children}
          </Link>
        );
      },
    },
    block: {
      normal: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant='body1' component='p' sx={{ mb: 1.5 }}>
          {children}
        </Typography>
      ),
      h1: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant='h4' gutterBottom sx={{ mt: 2 }}>
          {children}
        </Typography>
      ),
      h2: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant='h5' gutterBottom sx={{ mt: 2 }}>
          {children}
        </Typography>
      ),
      h3: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant='h6' gutterBottom sx={{ mt: 1.5 }}>
          {children}
        </Typography>
      ),
    },
    list: {
      bullet: ({ children }: { children?: React.ReactNode }) => (
        <Box component='ul' sx={{ pl: 3, mb: 1.5 }}>
          {children}
        </Box>
      ),
      number: ({ children }: { children?: React.ReactNode }) => (
        <Box component='ol' sx={{ pl: 3, mb: 1.5 }}>
          {children}
        </Box>
      ),
    },
    listItem: {
      bullet: ({ children }: { children?: React.ReactNode }) => (
        <Typography component='li' variant='body1' sx={{ mb: 0.5 }}>
          {children}
        </Typography>
      ),
      number: ({ children }: { children?: React.ReactNode }) => (
        <Typography component='li' variant='body1' sx={{ mb: 0.5 }}>
          {children}
        </Typography>
      ),
    },
  };

  return (
    <Box sx={{ color: theme.palette.text.primary }}>
      <PortableText value={processedContent} components={components} />
    </Box>
  );
};

PortableTextViewer.displayName = 'PortableTextViewer';
export default PortableTextViewer;
